import { FC, useRef, useState } from 'react'
import { PixelCrop, ReactCrop } from 'react-image-crop'
import useCroppedImage from './hooks/useCroppedImage'
import { cn } from '@wellesley/ui'
import useCropSettings from './hooks/useCropSettings'
import getCropSettings from './helpers/getCropSettings'
import { CropImageProps } from './types'
import usePanZoom from './hooks/usePanZoom'
import getPanBounds from './helpers/getPanBounds'
import adjustCropToImage from './helpers/adjustCropToImage'

import './cropImage.css'

type Sizes = {
  width: number
  height: number
}

const CropImage: FC<CropImageProps> = ({
  src,
  sizes,
  aspect = 1,
  circularCrop,
  zoom = 1,
  pan,
  imgMaxHeight,
  className,
  onChange,
  onMinZoomChange,
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const [crop, setCrop] = useCropSettings({
    sizes,
    aspect,
    imgMaxHeight,
  })

  const {
    pan: internalPan,
    onPointerDown,
    isDragging,
  } = usePanZoom({
    zoom,
    initialPan: pan,
    getBounds: () =>
      getPanBounds({
        image: imageRef.current,
        crop,
        zoom,
        containerWidth: containerRef.current?.offsetWidth,
        containerHeight: containerRef.current?.offsetHeight,
      }),
  })
  const effectivePan = pan ?? internalPan

  const [imageRef, setCompletedCrop] = useCroppedImage({
    zoom,
    onChange,
    pan: effectivePan,
  })

  const mapCropToImage = (next: PixelCrop): PixelCrop =>
    adjustCropToImage({
      crop: next,
      image: imageRef.current,
      containerWidth: containerRef.current?.offsetWidth,
      containerHeight: containerRef.current?.offsetHeight,
    })

  const handleLoad = () => {
    const image = imageRef.current
    setIsImageLoaded(true)

    if (image) {
      const initialCrop = getCropSettings(
        { width: image.width, height: image.height } as Sizes,
        aspect,
        imgMaxHeight ?? image.height,
      )
      setCrop(initialCrop)
      setCompletedCrop(mapCropToImage(initialCrop as PixelCrop))
      const minZoom = Math.max(
        (initialCrop.width ?? 0) / image.width,
        (initialCrop.height ?? 0) / image.height,
      )
      onMinZoomChange?.(minZoom)
    } else if (crop) {
      setCompletedCrop(crop as PixelCrop)
    }
  }

  return (
    <div ref={containerRef}>
      {isImageLoaded ? (
        <ReactCrop
          crop={crop}
          aspect={aspect}
          circularCrop={circularCrop}
          keepSelection={true}
          locked={true}
          onChange={(c) => setCompletedCrop(mapCropToImage(c))}
        >
          <img
            src={src}
            ref={imageRef}
            crossOrigin="anonymous"
            style={{
              maxHeight: imgMaxHeight ? `${imgMaxHeight}px` : 'auto',
              transform: `translate(${effectivePan.x}px, ${effectivePan.y}px) scale(${zoom})`,
              transformOrigin: 'center center',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            className={cn('mx-auto my-0', className)}
            onLoad={handleLoad}
            onPointerDown={onPointerDown}
          />
        </ReactCrop>
      ) : (
        <img
          src={src}
          ref={imageRef}
          crossOrigin="anonymous"
          style={{ maxHeight: imgMaxHeight ? `${imgMaxHeight}px` : 'auto' }}
          className={cn('mx-auto my-0', className)}
          onLoad={handleLoad}
        />
      )}
    </div>
  )
}

export default CropImage
